import InputInline from "../components/InputInline";
import { useEffect, useState } from "react";
import { fromJS } from "immutable";
import { getFieldIcon } from "../constants/fieldIconMap";
import Collapse from "../board/components/TaskDetail/Collapse";
import { useTranslation } from "react-i18next";

/** Render the appropriate Lucide icon for a given field (delegates to centralized fieldIconMap) */
export const renderFieldIcon = (field: any, className = "w-4 h-4 text-gray-600") => {
  return getFieldIcon(field, className);
};

export default function FieldList({ fields, formType = "SALE", onAddNewField, onShowEditField }) {
  const { t } = useTranslation();
  const [keywords, setKeywords] = useState(undefined);
  const [systemFields, setSystemFields] = useState(fromJS([]));
  const [customFields, setCustomFields] = useState(fromJS([]));

  const getFields = () => {
    return fields.filter((item) => item.get("formType") == formType);
  };

  const reload = () => {
    const filteredSystemFields = getFields().filter((item) => !item.get("isCustomField"));
    const filteredCustomFields = getFields().filter((item) => item.get("isCustomField"));
    setSystemFields(filteredSystemFields);
    setCustomFields(filteredCustomFields);
  };

  useEffect(() => {
    reload();
  }, [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reload();
  }, [formType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (keywords && keywords != "") {
      const filtered = getFields().filter(
        (item) => item.get("label").includes(keywords) && item.get("isCustomField")
      );
      const systems = getFields().filter(
        (item) => item.get("label").includes(keywords) && !item.get("isCustomField")
      );
      setSystemFields(systems);
      setCustomFields(filtered);
    } else {
      reload();
    }
  }, [keywords]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderCustomFields = () => {
    if (customFields.size > 0) {
      return <FieldTable fields={customFields} onShowEditField={onShowEditField} />;
    }
    return <div className={"font-bold p-8"}>{t("No lead/deal custom fields yet")}</div>;
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">{t("Fields")}</h1>
          <p className="mt-2 text-sm text-gray-700">
            {t(
              "Manage the fields for your workspace. These fields will be available for all deals, contacts, and accounts."
            )}
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            className="block rounded-md bg-blue-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            onClick={() => onAddNewField(formType)}
          >
            {t("Add Field")}
          </button>
        </div>
      </div>
      <div className="mt-8 flow-root">
        {/* <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8"> */}
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <InputInline
            value={keywords}
            placeholder={t("Search...")}
            onFinish={(value) => {
              setKeywords(value);
            }}
          />
          <Collapse title={t("Default fields")} expand={true}>
            <FieldTable fields={systemFields} onShowEditField={() => {}} />
          </Collapse>
          <Collapse title={t("Custom fields")} expand={true}>
            {renderCustomFields()}
          </Collapse>
        </div>
        {/* </div> */}
      </div>
    </div>
  );
}

const CapabilityBadge = ({ value }: { value: boolean }) => {
  const { t } = useTranslation();
  return (
    <span
      className={
        value
          ? "inline-flex items-center rounded-md bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
          : "inline-flex items-center rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"
      }
    >
      {value ? t("Yes") : t("No")}
    </span>
  );
};

const FieldTable = ({ fields, onShowEditField }) => {
  const { t } = useTranslation();
  const renderEditAction = (field) => {
    if (!field.get("isCustomField")) {
      return <div></div>;
    }
    return (
      <div
        className="text-blue-600 hover:text-blue-900 cursor-pointer"
        onClick={() => {
          onShowEditField(field.get("id"), field.get("formType"));
        }}
      >
        {t("Edit")}
      </div>
    );
  };
  const isEditable = (field) => field.get("isEditable") !== false;
  const isSearchable = (field) => field.get("isSearchable") !== false;
  const isEnrichable = (field) => field.get("isEnrichable") === true;
  return (
    <div className={"overflow-y-auto max-h-96"}>
      <table className="min-w-full divide-y divide-gray-300">
        <thead>
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
            >
              {t("Field name")}
            </th>
            <th
              scope="col"
              className="py-3.5 pl-3 pr-2 text-left text-sm font-semibold text-gray-900"
            >
              {t("Editable")}
            </th>
            <th
              scope="col"
              className="py-3.5 pl-2 pr-2 text-left text-sm font-semibold text-gray-900"
            >
              {t("Searchable")}
            </th>
            <th
              scope="col"
              className="py-3.5 pl-2 pr-3 text-left text-sm font-semibold text-gray-900"
            >
              {t("Enrichable")}
            </th>
            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 overflow-y-auto">
          {fields.map((field) => (
            <tr key={field.get("id")}>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <div className={"w-full flex space-x-1 align-middle"}>
                  {renderFieldIcon(field.toJS())}
                  <div className={"h-full"}>
                    <div>{field.get("label")}</div>
                    <div>{t(field.get("type"))}</div>
                  </div>
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm">
                <CapabilityBadge value={isEditable(field)} />
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm">
                <CapabilityBadge value={isSearchable(field)} />
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm">
                <CapabilityBadge value={isEnrichable(field)} />
              </td>
              <td className="relative py-4 pl-3 pr-4 text-center text-sm font-medium sm:pr-0">
                {renderEditAction(field)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
